import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, MessageSquare, Search, Users as UsersIcon, LogOut, GraduationCap, Megaphone, Send } from "lucide-react";
import { toast } from "sonner";

export default function TrainerClassStudents() {
    const { id } = useParams();
    const nav = useNavigate();
    const { logout } = useAuth();
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [bcOpen, setBcOpen] = useState(false);
    const [bcText, setBcText] = useState("");
    const [bcIncludeParents, setBcIncludeParents] = useState(false);
    const [bcBusy, setBcBusy] = useState(false);
    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        api.get(`/trainer/classes/${id}/students`)
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)));
    }, [id]);

    if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">جاري التحميل...</div>;

    const filtered = data.students.filter((s) =>
        !q.trim() || (s.full_name || "").toLowerCase().includes(q.trim().toLowerCase())
    );

    const openChat = (userId) => nav(`/messages?to=${encodeURIComponent(userId)}`);

    const sendBroadcast = async () => {
        const content = bcText.trim();
        if (!content) return toast.error("اكتبي نص الرسالة");
        setBcBusy(true);
        try {
            const { data: r } = await api.post(`/trainer/classes/${id}/broadcast`, {
                content, include_parents: bcIncludeParents,
            });
            toast.success(`تم إرسال الرسالة إلى ${r.delivered} حساب`);
            setBcOpen(false); setBcText(""); setBcIncludeParents(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBcBusy(false); }
    };

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => nav("/trainer")} data-testid="btn-back-trainer" className="text-white/80 hover:text-white hover:bg-white/10">
                            <ArrowRight className="w-4 h-4 ml-1" /> لوحتي
                        </Button>
                        <BrandLogo size={36} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10">
                        <LogOut className="w-4 h-4 ml-1" />
                        <span className="hidden sm:inline">خروج</span>
                    </Button>
                </div>
            </header>

            <main className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5">
                <div className="relative rounded-2xl overflow-hidden bg-gradient-to-l from-[#1B2A4E] to-[#0F172A] text-white">
                    {data.class.cover_url && (
                        <img
                            src={`${BACKEND}${data.class.cover_url}`}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover opacity-30"
                        />
                    )}
                    <div className="relative p-6 sm:p-8">
                        <div className="flex items-center gap-2 mb-2 text-white/70 text-xs uppercase tracking-widest">
                            <GraduationCap className="w-4 h-4 text-[#C8102E]" />
                            <span>طالبات الفصل</span>
                        </div>
                        <h1 className="text-3xl font-bold">{data.class.name}</h1>
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                            <div className="flex items-center gap-2 text-sm text-white/80">
                                <UsersIcon className="w-4 h-4" />
                                <span className="font-mono-num">{data.students.length}</span>
                                <span>طالبة</span>
                            </div>
                            <Button
                                onClick={() => setBcOpen(true)}
                                data-testid="btn-open-broadcast"
                                size="sm"
                                className="bg-[#C8102E] hover:bg-[#9F1239] text-white shadow"
                            >
                                <Megaphone className="w-4 h-4 ml-1" />
                                رسالة جماعية للفصل
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="relative max-w-md">
                    <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="ابحثي عن طالبة بالاسم..."
                        data-testid="class-students-search"
                        className="pr-10 h-11"
                    />
                </div>

                {filtered.length === 0 ? (
                    <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-slate-200">
                        {data.students.length === 0 ? "لا توجد طالبات في هذا الفصل بعد" : "لا نتائج مطابقة للبحث"}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {filtered.map((s) => (
                            <div
                                key={s.id}
                                data-testid={`student-card-${s.id}`}
                                className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 hover:shadow-md transition-all"
                            >
                                <Avatar url={s.avatar_url} name={s.full_name} seed={s.full_name} role="student" size={52} />
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-[#0F172A] truncate">{s.full_name}</div>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        {!s.active && <Badge variant="outline" className="text-xs text-slate-500">موقوفة</Badge>}
                                        {s.parent?.user_id && <Badge variant="outline" className="text-xs">ولي الأمر متاح</Badge>}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1 shrink-0">
                                    <Button
                                        size="sm"
                                        onClick={() => openChat(s.user_id)}
                                        data-testid={`btn-msg-student-${s.id}`}
                                        className="bg-[#1B2A4E] hover:bg-[#0F172A] h-9"
                                    >
                                        <MessageSquare className="w-4 h-4 ml-1" />
                                        رسالة
                                    </Button>
                                    {s.parent?.user_id && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => openChat(s.parent.user_id)}
                                            data-testid={`btn-msg-parent-${s.id}`}
                                            className="h-8 text-xs"
                                        >
                                            ولي الأمر
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            <Dialog open={bcOpen} onOpenChange={setBcOpen}>
                <DialogContent dir="rtl" className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Megaphone className="w-5 h-5 text-[#C8102E]" />
                            رسالة جماعية — {data.class.name}
                        </DialogTitle>
                        <DialogDescription>
                            ستُرسل الرسالة كمحادثة مستقلة لكل طالبة في هذا الفصل. يظهر إشعار Push لكل مستلم.
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea
                        value={bcText}
                        onChange={(e) => setBcText(e.target.value)}
                        placeholder="اكتبي رسالتك للفصل..."
                        rows={5}
                        data-testid="broadcast-textarea"
                        className="text-base"
                    />
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={bcIncludeParents}
                            onChange={(e) => setBcIncludeParents(e.target.checked)}
                            data-testid="broadcast-include-parents"
                            className="w-4 h-4 accent-[#1B2A4E]"
                        />
                        إرسال نسخة إلى أولياء الأمور أيضاً
                    </label>
                    <div className="text-xs text-slate-500 font-mono-num" dir="ltr">
                        {bcText.length} / 2000
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setBcOpen(false)} disabled={bcBusy}>إلغاء</Button>
                        <Button
                            onClick={sendBroadcast}
                            disabled={bcBusy || !bcText.trim()}
                            data-testid="btn-send-broadcast"
                            className="bg-[#C8102E] hover:bg-[#9F1239]"
                        >
                            <Send className="w-4 h-4 ml-1" />
                            {bcBusy ? "جارٍ الإرسال..." : `إرسال إلى ${data.students.length} طالبة`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
